import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Search,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  RefreshCcw,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { AdminShell } from "@/components/admin/admin-shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/admin/approvals/")({
  head: () => ({
    meta: [
      { title: "Partner Approvals — Spayzo Admin" },
      {
        name: "description",
        content:
          "Review and manage merchant partner applications across the Spayzo platform.",
      },
    ],
  }),
  component: Page,
});

type Status = "Pending" | "In Review" | "Approved" | "Rejected";
type SortKey = "newest" | "oldest" | "name";

interface Application {
  id: string;
  name: string;
  email: string;
  businessType: string;
  submitted: string;
  submittedAt: number; // unix ms for sorting
  submittedRelativeKey: string;
  documents: Array<"ID" | "License" | "Tax">;
  status: Status;
}

const applications: Application[] = [
  {
    id: "1",
    name: "Rami's Lounge",
    email: "rami@ramis-lounge.com",
    businessType: "Café & Lounge",
    submitted: "Jun 12, 2025",
    submittedAt: new Date("2025-06-12T14:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.hours2",
    documents: ["ID", "License", "Tax"],
    status: "Pending",
  },
  {
    id: "2",
    name: "Studio Bloom",
    email: "hello@studiobloom.co",
    businessType: "Creative Studio",
    submitted: "Jun 12, 2025",
    submittedAt: new Date("2025-06-12T11:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.hours5",
    documents: ["ID", "License", "Tax"],
    status: "In Review",
  },
  {
    id: "3",
    name: "Zenith Cowork",
    email: "ops@zenithcowork.io",
    businessType: "Co-working Space",
    submitted: "Jun 11, 2025",
    submittedAt: new Date("2025-06-11T09:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.day1",
    documents: ["ID", "License", "Tax"],
    status: "Pending",
  },
  {
    id: "4",
    name: "Nomad Hub",
    email: "team@nomadhub.net",
    businessType: "Co-working Space",
    submitted: "Jun 11, 2025",
    submittedAt: new Date("2025-06-11T08:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.day1",
    documents: ["ID", "License", "Tax"],
    status: "In Review",
  },
  {
    id: "5",
    name: "The Cove Studios",
    email: "cove@covestudios.com",
    businessType: "Event Venue",
    submitted: "Jun 10, 2025",
    submittedAt: new Date("2025-06-10T09:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.days2",
    documents: ["ID", "License", "Tax"],
    status: "Pending",
  },
  {
    id: "6",
    name: "Velvet Garden",
    email: "info@velvetgarden.co",
    businessType: "Photography Studio",
    submitted: "Jun 9, 2025",
    submittedAt: new Date("2025-06-09T09:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.days3",
    documents: ["ID", "License", "Tax"],
    status: "Approved",
  },
  {
    id: "7",
    name: "Pixel Loft",
    email: "admin@pixelloft.io",
    businessType: "Café & Lounge",
    submitted: "Jun 8, 2025",
    submittedAt: new Date("2025-06-08T09:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.days4",
    documents: ["ID", "License", "Tax"],
    status: "Rejected",
  },
  {
    id: "8",
    name: "Mosaic Bistro",
    email: "chef@mosaicbistro.com",
    businessType: "Restaurant",
    submitted: "Jun 6, 2025",
    submittedAt: new Date("2025-06-06T09:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.days6",
    documents: ["ID", "License", "Tax"],
    status: "Approved",
  },
  {
    id: "9",
    name: "Orbit Tech Hub",
    email: "hi@orbithub.tech",
    businessType: "Tech Hub",
    submitted: "Jun 5, 2025",
    submittedAt: new Date("2025-06-05T09:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.days7",
    documents: ["ID", "License", "Tax"],
    status: "Pending",
  },
  {
    id: "10",
    name: "Lumen Photo",
    email: "book@lumenphoto.co",
    businessType: "Photography Studio",
    submitted: "Jun 4, 2025",
    submittedAt: new Date("2025-06-04T09:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.days8",
    documents: ["ID", "License", "Tax"],
    status: "In Review",
  },
  {
    id: "11",
    name: "Harbor Events",
    email: "sales@harborevents.co",
    businessType: "Event Venue",
    submitted: "Jun 3, 2025",
    submittedAt: new Date("2025-06-03T09:00:00Z").getTime(),
    submittedRelativeKey: "adminApprovals.relative.days9",
    documents: ["ID", "License", "Tax"],
    status: "Pending",
  },
];

function useStatusLabel() {
  const { t } = useTranslation();
  const map: Record<Status, string> = {
    Pending: t("adminApprovals.status.pending"),
    "In Review": t("adminApprovals.status.inReview"),
    Approved: t("adminApprovals.status.approved"),
    Rejected: t("adminApprovals.status.rejected"),
  };
  return (s: Status) => map[s];
}

function statusBadge(status: Status, label: string) {
  const map: Record<Status, string> = {
    Pending: "bg-amber-50 text-amber-700 border-amber-200",
    "In Review": "bg-sky-50 text-sky-700 border-sky-200",
    Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Rejected: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <Badge variant="outline" className={`rounded-full font-medium ${map[status]}`}>
      {label}
    </Badge>
  );
}

function Page() {
  const { t } = useTranslation();
  const statusLabel = useStatusLabel();
  const [tab, setTab] = useState<"all" | Status>("all");
  const [businessFilter, setBusinessFilter] = useState("all");
  const [sort, setSort] = useState<SortKey>("newest");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const statusTabs: Array<{ key: "all" | Status; label: string; count: number }> = [
    { key: "all", label: t("common.all"), count: applications.length },
    {
      key: "Pending",
      label: statusLabel("Pending"),
      count: applications.filter((a) => a.status === "Pending").length,
    },
    {
      key: "In Review",
      label: statusLabel("In Review"),
      count: applications.filter((a) => a.status === "In Review").length,
    },
    {
      key: "Approved",
      label: statusLabel("Approved"),
      count: applications.filter((a) => a.status === "Approved").length,
    },
    {
      key: "Rejected",
      label: statusLabel("Rejected"),
      count: applications.filter((a) => a.status === "Rejected").length,
    },
  ];

  const kpis = [
    {
      label: t("adminApprovals.kpis.pendingReview.label"),
      value: applications.filter((a) => a.status === "Pending").length.toString(),
      hint: t("adminApprovals.kpis.pendingReview.hint"),
      icon: Clock,
      tone: "text-amber-700",
      ring: "bg-amber-100",
      gradient: "from-amber-50 to-white",
    },
    {
      label: t("adminApprovals.kpis.inReview.label"),
      value: applications.filter((a) => a.status === "In Review").length.toString(),
      hint: t("adminApprovals.kpis.inReview.hint"),
      icon: Eye,
      tone: "text-sky-700",
      ring: "bg-sky-100",
      gradient: "from-sky-50 to-white",
    },
    {
      label: t("adminApprovals.kpis.approvedTotal.label"),
      value: "124",
      hint: t("adminApprovals.kpis.approvedTotal.hint"),
      icon: CheckCircle2,
      tone: "text-emerald-700",
      ring: "bg-emerald-100",
      gradient: "from-emerald-50 to-white",
    },
    {
      label: t("adminApprovals.kpis.rejectedTotal.label"),
      value: "18",
      hint: t("adminApprovals.kpis.rejectedTotal.hint"),
      icon: XCircle,
      tone: "text-rose-700",
      ring: "bg-rose-100",
      gradient: "from-rose-50 to-white",
    },
  ];

  const businessTypes = useMemo(
    () => Array.from(new Set(applications.map((a) => a.businessType))).sort(),
    [],
  );

  const filtered = useMemo(() => {
    const list = applications.filter((a) => {
      if (tab !== "all" && a.status !== tab) return false;
      if (businessFilter !== "all" && a.businessType !== businessFilter) return false;
      if (query) {
        const q = query.toLowerCase();
        if (
          !a.name.toLowerCase().includes(q) &&
          !a.email.toLowerCase().includes(q) &&
          !a.businessType.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
    const sorted = [...list];
    if (sort === "newest") sorted.sort((a, b) => b.submittedAt - a.submittedAt);
    else if (sort === "oldest") sorted.sort((a, b) => a.submittedAt - b.submittedAt);
    else if (sort === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [tab, businessFilter, query, sort]);

  const pendingCount = applications.filter((a) => a.status === "Pending").length;

  const allSelected = filtered.length > 0 && filtered.every((a) => selected.has(a.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) filtered.forEach((a) => next.delete(a.id));
    else filtered.forEach((a) => next.add(a.id));
    setSelected(next);
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const clearFilters = () => {
    setTab("all");
    setBusinessFilter("all");
    setQuery("");
    setSort("newest");
  };

  return (
    <AdminShell
      title={t("adminApprovals.title")}
      subtitle={t("adminApprovals.subtitle")}
    >
      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <Card
            key={k.label}
            className={`rounded-2xl border-border/60 bg-gradient-to-br ${k.gradient} p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-md`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {k.label}
                </p>
                <p className="mt-2 font-display text-3xl font-bold tracking-tight">
                  {k.value}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{k.hint}</p>
              </div>
              <span
                className={`grid h-11 w-11 place-items-center rounded-2xl ${k.ring} ${k.tone} shadow-inner`}
              >
                <k.icon className="h-5 w-5" />
              </span>
            </div>
          </Card>
        ))}
      </div>

      {/* Main table */}
      <div className="mt-6">
        <Card className="overflow-hidden rounded-2xl border-border/60 shadow-soft">
          {/* Table header controls */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 bg-gradient-to-r from-surface/60 to-transparent p-4">
            <div className="flex items-center gap-3">
              <span
                className="grid h-10 w-10 place-items-center rounded-xl text-white"
                style={{ backgroundColor: "#5958F3" }}
              >
                <Users className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-display text-lg font-semibold">{t("adminApprovals.tableHeader.title")}</h2>
                <p className="text-xs text-muted-foreground">
                  {t("adminApprovals.tableHeader.showing", { count: filtered.length, total: applications.length, pending: pendingCount })}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selected.size > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t("adminApprovals.tableHeader.selected", { count: selected.size })}
                </span>
              )}
              <ConfirmAction
                kind="approve"
                subject={t("adminApprovals.tableHeader.applicationsCount", { count: selected.size })}
                trigger={
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9 rounded-lg"
                    disabled={selected.size === 0}
                  >
                    <CheckCircle2 className="h-4 w-4" /> {t("adminApprovals.tableHeader.bulkApprove")}
                  </Button>
                }
              />
              <ConfirmAction
                kind="reject"
                subject={t("adminApprovals.tableHeader.applicationsCount", { count: selected.size })}
                trigger={
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9 rounded-lg text-rose-600 hover:text-rose-700"
                    disabled={selected.size === 0}
                  >
                    <XCircle className="h-4 w-4" /> {t("adminApprovals.tableHeader.bulkReject")}
                  </Button>
                }
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border/60 px-4 py-3">
            {statusTabs.map((tItem) => {
              const active = tab === tItem.key;
              return (
                <button
                  key={tItem.key}
                  onClick={() => setTab(tItem.key)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                    active
                      ? "border-transparent text-white shadow-soft"
                      : "border-border/60 bg-background text-muted-foreground hover:bg-surface hover:text-foreground"
                  }`}
                  style={active ? { backgroundColor: "#5958F3" } : undefined}
                >
                  {tItem.label}
                  <span
                    className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
                      active ? "bg-white/20 text-white" : "bg-surface text-foreground"
                    }`}
                  >
                    {tItem.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border/60 p-4">
            <div className="relative min-w-[240px] flex-1">
              <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("adminApprovals.filters.searchPlaceholder")}
                className="h-10 rounded-xl border-border/70 bg-surface ps-10"
              />
            </div>
            <Select value={businessFilter} onValueChange={setBusinessFilter}>
              <SelectTrigger className="h-10 w-[200px] rounded-xl">
                <SelectValue placeholder={t("adminApprovals.filters.businessType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("adminApprovals.filters.allBusinessTypes")}</SelectItem>
                {businessTypes.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
              <SelectTrigger className="h-10 w-[150px] rounded-xl">
                <SelectValue placeholder={t("adminApprovals.filters.sort")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t("adminApprovals.filters.sortNewest")}</SelectItem>
                <SelectItem value="oldest">{t("adminApprovals.filters.sortOldest")}</SelectItem>
                <SelectItem value="name">{t("adminApprovals.filters.sortName")}</SelectItem>
              </SelectContent>
            </Select>
            {(tab !== "all" ||
              businessFilter !== "all" ||
              query ||
              sort !== "newest") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-10 rounded-xl text-xs"
              >
                {t("common.clear")}
              </Button>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-surface/60 hover:bg-surface/60">
                  <TableHead className="w-12 text-center">
                    <div className="flex justify-center">
                      <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                    </div>
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("adminApprovals.table.merchant")}
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("adminApprovals.table.businessType")}
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("adminApprovals.table.submitted")}
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("adminApprovals.table.documents")}
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("adminApprovals.table.status")}
                  </TableHead>
                  <TableHead className="text-end text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t("adminApprovals.table.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((a) => {
                  const decided = a.status === "Approved" || a.status === "Rejected";
                  return (
                    <TableRow key={a.id} className="align-top transition hover:bg-surface/40">
                      <TableCell className="text-center">
                        <div className="flex justify-center">
                          <Checkbox
                            checked={selected.has(a.id)}
                            onCheckedChange={() => toggleOne(a.id)}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div
                            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-xs font-semibold text-white shadow-sm"
                            style={{ backgroundColor: "#5958F3" }}
                          >
                            {a.name
                              .split(" ")
                              .map((s) => s[0])
                              .slice(0, 2)
                              .join("")}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold">{a.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {a.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{a.businessType}</TableCell>
                      <TableCell>
                        <p className="text-sm">{a.submitted}</p>
                        <p className="text-xs text-muted-foreground">
                          {t(a.submittedRelativeKey)}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {a.documents.map((d) => (
                            <span
                              key={d}
                              className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-surface px-2 py-0.5 text-[11px] font-medium"
                            >
                              <FileText className="h-3 w-3" /> {d}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>{statusBadge(a.status, statusLabel(a.status))}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1.5">
                          {decided ? (
                            <>
                              <Button
                                asChild
                                size="sm"
                                variant="outline"
                                className="h-8 rounded-lg"
                              >
                                <Link to="/admin/approvals/$id" params={{ id: a.id }}>
                                  <Eye className="h-3.5 w-3.5" /> {t("common.view")}
                                </Link>
                              </Button>
                              <ConfirmAction
                                kind={a.status === "Approved" ? "revoke" : "rereview"}
                                subject={a.name}
                                trigger={
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className={`h-8 rounded-lg ${a.status === "Approved" ? "text-rose-600 hover:text-rose-700" : ""}`}
                                  >
                                    <RefreshCcw className="h-3.5 w-3.5" />
                                    {a.status === "Approved" ? t("adminApprovals.table.revoke") : t("adminApprovals.table.rereview")}
                                  </Button>
                                }
                              />
                            </>
                          ) : (
                            <>
                              <Button
                                asChild
                                size="sm"
                                variant="outline"
                                className="h-8 rounded-lg"
                              >
                                <Link to="/admin/approvals/$id" params={{ id: a.id }}>
                                  <Eye className="h-3.5 w-3.5" /> {t("adminApprovals.table.review")}
                                </Link>
                              </Button>
                              <ConfirmAction
                                kind="approve"
                                subject={a.name}
                                trigger={
                                  <Button
                                    size="sm"
                                    className="h-8 rounded-lg text-white hover:opacity-90"
                                    style={{ backgroundColor: "#5958F3" }}
                                  >
                                    <CheckCircle2 className="h-3.5 w-3.5" /> {t("adminApprovals.table.approve")}
                                  </Button>
                                }
                              />
                              <ConfirmAction
                                kind="reject"
                                subject={a.name}
                                trigger={
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 rounded-lg text-rose-600 hover:text-rose-700"
                                  >
                                    <XCircle className="h-3.5 w-3.5" /> {t("adminApprovals.table.reject")}
                                  </Button>
                                }
                              />
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-16 text-center text-sm text-muted-foreground"
                    >
                      <div className="mx-auto flex max-w-sm flex-col items-center gap-2">
                        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-surface">
                          <Search className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <p className="font-medium text-foreground">{t("adminApprovals.empty.title")}</p>
                        <p className="text-xs">
                          {t("adminApprovals.empty.description")}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearFilters}
                          className="mt-2 rounded-lg"
                        >
                          {t("adminApprovals.filters.clearFilters")}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between border-t border-border/60 p-4 text-xs text-muted-foreground">
            <span>
              {t("adminApprovals.pagination.showing", { count: filtered.length, total: applications.length })}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-8 rounded-lg" disabled>
                {t("common.previous")}
              </Button>
              <Button size="sm" variant="outline" className="h-8 rounded-lg">
                {t("common.next")}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AdminShell>
  );
}

function ConfirmAction({
  kind,
  subject,
  trigger,
}: {
  kind: "approve" | "reject" | "revoke" | "rereview";
  subject: string;
  trigger: React.ReactNode;
}) {
  const { t } = useTranslation();
  const config = {
    approve: {
      title: t("adminApprovals.confirm.approve.title"),
      description: t("adminApprovals.confirm.approve.description", { subject }),
      actionLabel: t("adminApprovals.confirm.approve.action"),
      destructive: false,
    },
    reject: {
      title: t("adminApprovals.confirm.reject.title"),
      description: t("adminApprovals.confirm.reject.description", { subject }),
      actionLabel: t("adminApprovals.confirm.reject.action"),
      destructive: true,
    },
    revoke: {
      title: t("adminApprovals.confirm.revoke.title"),
      description: t("adminApprovals.confirm.revoke.description", { subject }),
      actionLabel: t("adminApprovals.confirm.revoke.action"),
      destructive: true,
    },
    rereview: {
      title: t("adminApprovals.confirm.rereview.title"),
      description: t("adminApprovals.confirm.rereview.description", { subject }),
      actionLabel: t("adminApprovals.confirm.rereview.action"),
      destructive: false,
    },
  }[kind];

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{config.title}</AlertDialogTitle>
          <AlertDialogDescription>{config.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className={
              config.destructive
                ? "bg-rose-600 text-white hover:bg-rose-700"
                : "text-white hover:opacity-90"
            }
            style={config.destructive ? undefined : { backgroundColor: "#5958F3" }}
          >
            {config.actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
